/**
 * WordPress dependencies
 */
import { Tooltip } from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import { getMergedValue, getAllowedSides, LABELS } from './utils';
import {
	FlexedBoxControlIcon,
	SelectWrapper,
	StyledSelectControl,
} from './styles/overflow-control-styles';
import type {
	OverflowControlSelectControlProps,
	OverflowControlValue,
} from './types';

const noop = () => {};

function getSidesToModify(
	side: OverflowControlSelectControlProps[ 'side' ],
	sides: OverflowControlSelectControlProps[ 'sides' ]
) {
	const allowedSides = getAllowedSides( sides );

	let modifiedSides: ( keyof OverflowControlValue )[] = [];
	switch ( side ) {
		case 'all':
			modifiedSides = [ 'horizontal', 'vertical' ];
			break;
		case 'horizontal':
			modifiedSides = [ 'horizontal' ];
			break;
		case 'vertical':
			modifiedSides = [ 'vertical' ];
			break;
		default:
			modifiedSides = [ side ];
	}

	return modifiedSides.filter( ( s ) => allowedSides.has( s ) );
}

export default function OverflowSelectControl( {
	onChange = noop,
	values,
	sides,
	side,
	disabledSides,
	...props
}: OverflowControlSelectControlProps ): JSX.Element {
	const defaultValuesToModify = getSidesToModify( side, sides );

	const handleOnChange = ( nextValues: OverflowControlValue ) => {
		onChange( nextValues );
	};

	const handleOnValueChange = ( next?: string ) => {
		const nextValues = { ...values };
		const nextValue = next;
		const modifiedSides = getSidesToModify( side, sides );

		modifiedSides.forEach( ( modifiedSide ) => {
			nextValues[ modifiedSide ] = nextValue;
		} );

		handleOnChange( nextValues );
	};

	const mergedValue = getMergedValue( values, defaultValuesToModify );

	return (
		<SelectWrapper key={ `box-control-${ side }` } expanded>
			<FlexedBoxControlIcon side={ side } sides={ sides } />
			<Tooltip placement="top-end" text={ LABELS[ side ] }>
				<StyledSelectControl
					{ ...props }
					className="component-box-control__select-control"
					disabled={
						( [ 'vertical', 'horizontal' ].includes( side ) &&
							disabledSides[
								side as keyof OverflowControlValue
							] ) ||
						( side === 'all' &&
							disabledSides.vertical &&
							disabledSides.horizontal )
					}
					value={ mergedValue }
					options={ [
						{
							value: 'visible',
							label: _x( 'Visible', 'text', 'nelio-ab-testing' ),
						},
						{
							value: 'hidden',
							label: _x( 'Hidden', 'text', 'nelio-ab-testing' ),
						},
						{
							value: 'clip',
							label: _x( 'Clip', 'text', 'nelio-ab-testing' ),
						},
						{
							value: 'scroll',
							label: _x( 'Scroll', 'text', 'nelio-ab-testing' ),
						},
						{
							value: 'auto',
							label: _x( 'Auto', 'text', 'nelio-ab-testing' ),
						},
					] }
					onChange={ handleOnValueChange }
					label={ LABELS[ side ] }
					hideLabelFromVision
				/>
			</Tooltip>
		</SelectWrapper>
	);
}
